/* Deterrent sound synthesizers + Agora delivery. Lets the resident pick
 * the right "scare" for the moment — large-dog bark for an intruder at
 * a gate, small-dog yip for a website chat-bait, police siren for a
 * commercial premise, or a harsh alarm horn for hotel-room safety.
 *
 * All sounds are 100 % synthesized via Web Audio API at call-time —
 * NO mp3 / wav assets are bundled, so the deterrent list can grow
 * freely without bloating the bundle.
 *
 * Each deterrent obeys the same Agora delivery contract:
 *   - synth → MediaStreamAudioDestinationNode → AgoraRTC custom track
 *   - mic temporarily unpublished while the deterrent plays
 *   - local side-tone so the resident gets confirmation
 *   - mute state preserved across the swap
 *
 *   await playDeterrentOnCall({ deterrentId, client, micRef, setBarking });
 *
 * Adding a new deterrent: add an entry to DETERRENTS + a render
 * function. UI picks it up automatically. */
import AgoraRTC from "agora-rtc-sdk-ng";

// User-facing catalogue. `iconName` is referenced by the UI to pick a
// lucide icon (kept as a string so this module has no React dep).
// `source` is either a URL (loaded + decoded once, cached) for sampled
// deterrents, or `null` for synthesized ones. Real dog vocalisations
// are too biologically complex to synthesize believably — sampled CC0
// recordings from SoundBible work much better. Sirens and alarms are
// inherently synthetic so we keep generating those at runtime.
export const DETERRENTS = [
  { id: "bark_small", label: "Dog bark",         shortLabel: "Bark",       iconName: "Dog",       durationS: 4.5, source: "/sounds/bark_small.mp3", hint: "Rapid dog barking — best for gates, homes and shops." },
  { id: "siren",      label: "Police siren",    shortLabel: "Siren",      iconName: "Siren",     durationS: 6.0, source: null,                    hint: "Yelp-pattern siren — commercial premises." },
  { id: "alarm",      label: "Alarm horn",      shortLabel: "Horn",       iconName: "Megaphone", durationS: 5.0, source: null,                    hint: "Pulsing air horn — hotel rooms & loud environments." },
];

export const DEFAULT_DETERRENT_ID = "bark_small";

// ---- Sample cache --------------------------------------------------
// Decoded AudioBuffers, keyed by URL. Avoids re-decoding the same mp3
// on every play. Cleared when the page unloads.
const _sampleCache = new Map();

async function loadSampleBuffer(ctx, url) {
  if (_sampleCache.has(url)) return _sampleCache.get(url);
  const res = await fetch(url, { credentials: "omit", cache: "force-cache" });
  if (!res.ok) throw new Error(`Failed to load deterrent sample: ${url} (${res.status})`);
  const arr = await res.arrayBuffer();
  // decodeAudioData returns a Promise in modern browsers; the callback
  // form is the legacy API. Wrap both for portability.
  const buffer = await new Promise((resolve, reject) => {
    try {
      const p = ctx.decodeAudioData(arr, resolve, reject);
      if (p && typeof p.then === "function") p.then(resolve, reject);
    } catch (e) { reject(e); }
  });
  _sampleCache.set(url, buffer);
  return buffer;
}

function clamp(x, lo, hi) { return Math.max(lo, Math.min(hi, x)); }

// =====================================================================
// DOG-BARK FAMILY — both large and small variants reuse the same
// 3-formant source-filter model; they differ in formant centres,
// fundamental range, and cadence. See playDogBark.js history for the
// original derivation.
// =====================================================================

function formantWeight(freq, formants, floor) {
  let w = floor;
  for (const { f, bw, g } of formants) {
    const x = (freq - f) / bw;
    w += g / (1 + x * x);
  }
  return w;
}

function renderDogBark(channelData, sampleRate, params) {
  const { pattern, formants, formantFloor, nHarmonics, f0Range, sweepUpRange, sweepEndRange, peakPosRange, decayTauRange } = params;
  for (const { at, len, intensity } of pattern) {
    const startSample = Math.floor(at * sampleRate);
    const lenSamples = Math.floor(len * sampleRate);
    const f0 = f0Range[0] + Math.random() * (f0Range[1] - f0Range[0]);
    const sweepUp = sweepUpRange[0] + Math.random() * (sweepUpRange[1] - sweepUpRange[0]);
    const sweepEnd = sweepEndRange[0] + Math.random() * (sweepEndRange[1] - sweepEndRange[0]);
    const peakPos = peakPosRange[0] + Math.random() * (peakPosRange[1] - peakPosRange[0]);
    const decayTau = decayTauRange[0] + Math.random() * (decayTauRange[1] - decayTauRange[0]);

    // Per-harmonic formant-weighted amplitudes, evaluated at f0.
    const harmonicAmp = new Float32Array(nHarmonics);
    let norm = 0;
    for (let h = 1; h <= nHarmonics; h++) {
      const w = formantWeight(h * f0, formants, formantFloor) / h;
      harmonicAmp[h - 1] = w;
      norm += w * w;
    }
    const scale = 0.85 / Math.sqrt(norm || 1);
    for (let h = 0; h < nHarmonics; h++) harmonicAmp[h] *= scale;

    // Attack-click transient (~5 ms band-shaped noise).
    const clickSamples = Math.min(lenSamples, Math.floor(0.005 * sampleRate));
    let clickPhase = 0;
    for (let i = 0; i < clickSamples; i++) {
      const idx = startSample + i;
      if (idx >= channelData.length) break;
      clickPhase = clickPhase * 0.4 + (Math.random() * 2 - 1) * 0.6;
      const env = 1 - i / clickSamples;
      channelData[idx] += clickPhase * env * 0.6 * intensity;
    }

    // Voiced body.
    let phase = 0;
    for (let i = 0; i < lenSamples; i++) {
      const idx = startSample + i;
      if (idx >= channelData.length) break;
      const t = i / lenSamples;
      let pitchRatio;
      if (t < peakPos) {
        pitchRatio = 1 + (sweepUp - 1) * (t / peakPos);
      } else {
        const u = (t - peakPos) / (1 - peakPos);
        pitchRatio = sweepUp * Math.pow(sweepEnd / sweepUp, u);
      }
      const freq = f0 * pitchRatio;
      phase += (freq * 2 * Math.PI) / sampleRate;
      // Sum harmonics via cos-recurrence: sin((h+1)x) = 2cos(x)sin(hx) - sin((h-1)x)
      const cp = Math.cos(phase);
      let s_prev = 0;
      let s_curr = Math.sin(phase);
      let signal = harmonicAmp[0] * s_curr;
      for (let h = 1; h < nHarmonics; h++) {
        const s_next = 2 * cp * s_curr - s_prev;
        signal += harmonicAmp[h] * s_next;
        s_prev = s_curr;
        s_curr = s_next;
      }
      const noise = (Math.random() * 2 - 1) * 0.18;
      const attack = Math.min(1, (i / sampleRate) / 0.004);
      const decay = Math.exp(-(i / sampleRate) / decayTau);
      channelData[idx] += (signal + noise) * attack * decay * intensity;
    }
  }
}

const LARGE_BARK_PARAMS = {
  pattern: [
    { at: 0.00, len: 0.30, intensity: 1.00 },
    { at: 0.55, len: 0.28, intensity: 0.92 },
    { at: 1.10, len: 0.32, intensity: 1.00 },
    { at: 2.00, len: 0.36, intensity: 1.00 },
    { at: 2.80, len: 0.32, intensity: 0.95 },
    { at: 3.70, len: 0.34, intensity: 1.00 },
    { at: 4.70, len: 0.40, intensity: 1.05 },
  ],
  formants: [
    { f: 720,  bw: 220, g: 1.00 },
    { f: 1500, bw: 320, g: 0.55 },
    { f: 2400, bw: 460, g: 0.28 },
  ],
  formantFloor: 0.08,
  nHarmonics: 14,
  f0Range:       [240, 380],
  sweepUpRange:  [1.18, 1.40],
  sweepEndRange: [0.78, 0.90],
  peakPosRange:  [0.18, 0.26],
  decayTauRange: [0.16, 0.24],
};

const SMALL_BARK_PARAMS = {
  // 10 quick yips over ~4.3s — characteristic chihuahua / yorkie cadence.
  // Tighter intervals + shorter bursts than the large-dog pattern.
  pattern: Array.from({ length: 10 }, (_, i) => ({
    at: 0.10 + i * 0.42,
    len: 0.18,
    intensity: 0.95 + (i % 2) * 0.08, // alternating slight loud/quiet
  })),
  // Formants shifted ~25% higher for a smaller vocal tract.
  formants: [
    { f: 900,  bw: 240, g: 1.00 },
    { f: 1900, bw: 350, g: 0.55 },
    { f: 2900, bw: 500, g: 0.30 },
  ],
  formantFloor: 0.10,
  nHarmonics: 12,
  f0Range:       [480, 720],
  sweepUpRange:  [1.30, 1.55],
  sweepEndRange: [0.82, 0.95],
  peakPosRange:  [0.15, 0.22],
  decayTauRange: [0.08, 0.13],
};

// =====================================================================
// POLICE-SIREN — yelp pattern: pitch alternates between two centres
// rapidly. Approximated by a sawtooth-rich tone whose frequency tracks
// a sawtooth modulation, with a slight amplitude tremolo.
// =====================================================================

function renderSiren(channelData, sampleRate, durationS) {
  const totalSamples = Math.floor(durationS * sampleRate);
  const F_LO = 700;
  const F_HI = 1400;
  const YELP_RATE_HZ = 3.5;            // 3.5 sweeps/sec
  const YELP_PERIOD_S = 1 / YELP_RATE_HZ;
  let phase = 0;
  for (let i = 0; i < totalSamples; i++) {
    if (i >= channelData.length) break;
    const t = i / sampleRate;
    // Sawtooth-shaped pitch sweep: ramps F_LO → F_HI then snaps back.
    const u = (t % YELP_PERIOD_S) / YELP_PERIOD_S;
    const freq = F_LO + (F_HI - F_LO) * u;
    phase += (freq * 2 * Math.PI) / sampleRate;
    // Mix sin + 2nd harmonic for siren timbre. Sine alone sounds too pure
    // for a real PA-driven siren.
    const fundamental = Math.sin(phase);
    const second = Math.sin(2 * phase) * 0.35;
    // Slight 7 Hz amplitude tremolo simulates the speaker rotation /
    // Doppler effect of an approaching siren.
    const tremolo = 0.85 + 0.15 * Math.sin(2 * Math.PI * 7 * t);
    // Soft-clip via tanh — gives the siren a "speaker-saturated" edge.
    const signal = Math.tanh(1.6 * (fundamental + second)) * tremolo;
    // Brief amplitude ramp at start + end to avoid click artefacts.
    const fade = clamp(t / 0.05, 0, 1) * clamp((durationS - t) / 0.1, 0, 1);
    channelData[i] += signal * 0.7 * fade;
  }
}

// =====================================================================
// ALARM HORN — harsh pulsing air horn / fire alarm sound. Square-rich
// waveform at ~260 Hz, pulsed 0.45 s ON / 0.12 s OFF.
// =====================================================================

function renderAlarm(channelData, sampleRate, durationS) {
  const totalSamples = Math.floor(durationS * sampleRate);
  const F0 = 262;          // ~C4
  const PULSE_ON = 0.45;
  const PULSE_OFF = 0.12;
  const PULSE_PERIOD = PULSE_ON + PULSE_OFF;
  let phase = 0;
  let phase2 = 0;
  // 2nd voice slightly detuned (3 Hz) → beat-frequency growl.
  const F0_DETUNE = F0 + 3;
  for (let i = 0; i < totalSamples; i++) {
    if (i >= channelData.length) break;
    const t = i / sampleRate;
    const inPulse = (t % PULSE_PERIOD) < PULSE_ON;
    phase  += (F0 * 2 * Math.PI) / sampleRate;
    phase2 += (F0_DETUNE * 2 * Math.PI) / sampleRate;
    if (!inPulse) continue;
    // Position within the pulse, used for short attack/release ramps.
    const pulseT = (t % PULSE_PERIOD) / PULSE_ON;
    const env = clamp(pulseT * 25, 0, 1) * clamp((1 - pulseT) * 25, 0, 1);
    // Sum first 5 odd harmonics (square-wave approximation) — harsher,
    // grates the ear, perfect for an alarm.
    let v = 0;
    for (let h = 1; h <= 9; h += 2) {
      v += Math.sin(h * phase) / h;
    }
    v += Math.sin(phase2) * 0.4; // detuned 2nd voice
    channelData[i] += Math.tanh(v * 0.95) * env * 0.85;
  }
}

// =====================================================================
// Dispatcher
// =====================================================================

function renderDeterrent(deterrentId, channelData, sampleRate, durationS) {
  switch (deterrentId) {
    case "bark_large":
      // Legacy id from earlier product versions — kept in the synth
      // dispatcher so saved user preferences don't blow up, but no
      // longer surfaced in the picker. Falls back to the same synth
      // path as the current dog-bark entry.
      renderDogBark(channelData, sampleRate, LARGE_BARK_PARAMS);
      break;
    case "bark_small":
      renderDogBark(channelData, sampleRate, SMALL_BARK_PARAMS);
      break;
    case "siren":
      renderSiren(channelData, sampleRate, durationS);
      break;
    case "alarm":
      renderAlarm(channelData, sampleRate, durationS);
      break;
    default:
      // Unknown id → silent buffer. UI shouldn't be able to hit this.
      break;
  }
  // Soft-clip the final buffer to ±1 — protects against any sum overflow
  // (especially the bark renderer which can transiently exceed 1.0).
  for (let i = 0; i < channelData.length; i++) {
    channelData[i] = Math.tanh(channelData[i] * 1.05);
  }
}

// =====================================================================
// Public entry point — same lifecycle contract as the original
// playDogBarkOnCall().
// =====================================================================

export async function playDeterrentOnCall({ deterrentId, client, micRef, setBarking }) {
  if (!client) throw new Error("Agora client not connected");
  const meta = DETERRENTS.find((d) => d.id === deterrentId) || DETERRENTS[0];
  setBarking?.(true);

  const Ctx = window.AudioContext || window.webkitAudioContext;
  const ctx = new Ctx();
  if (ctx.state === "suspended") {
    try { await ctx.resume(); } catch { /* noop */ }
  }
  const sampleRate = ctx.sampleRate;

  // Build the AudioBuffer — either decode the bundled sample (barks) or
  // generate the synthesized waveform inline (siren / alarm). If the
  // sample fetch/decode fails (file missing in deployment, network
  // hiccup), gracefully fall back to the synthesized renderer so the
  // resident still gets *something* on click.
  let buffer;
  let bufferDuration;
  if (meta.source) {
    try {
      buffer = await loadSampleBuffer(ctx, meta.source);
      bufferDuration = buffer.duration;
    } catch (e) {
      console.warn(`Deterrent sample load failed (${meta.source}), falling back to synthesis:`, e);
      bufferDuration = meta.durationS;
      buffer = ctx.createBuffer(1, Math.floor(bufferDuration * sampleRate), sampleRate);
      renderDeterrent(meta.id, buffer.getChannelData(0), sampleRate, bufferDuration);
    }
  } else {
    bufferDuration = meta.durationS;
    buffer = ctx.createBuffer(1, Math.floor(bufferDuration * sampleRate), sampleRate);
    renderDeterrent(meta.id, buffer.getChannelData(0), sampleRate, bufferDuration);
  }
  const holdMs = bufferDuration * 1000 + 400; // mic-republish grace

  // Routing graph: bufferSource → gain → splitter:
  //                                  ├─→ ctx.destination (local side-tone)
  //                                  └─→ MediaStreamDestination (Agora → visitor)
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  const gain = ctx.createGain();
  gain.gain.value = 1.0;
  const dest = ctx.createMediaStreamDestination();
  src.connect(gain);
  gain.connect(dest);
  gain.connect(ctx.destination);

  const mediaTrack = dest.stream.getAudioTracks()[0];
  const remoteTrack = AgoraRTC.createCustomAudioTrack({ mediaStreamTrack: mediaTrack });

  let wasMuted = false;
  if (micRef.current) {
    try { wasMuted = micRef.current.muted === true; } catch { /* noop */ }
  }

  const previousMic = micRef.current;
  try {
    if (previousMic) {
      try { await client.unpublish([previousMic]); } catch { /* best-effort */ }
    }
    await client.publish([remoteTrack]);
    src.start();
  } catch (e) {
    try { src.start(); } catch { /* noop */ }
    if (previousMic) {
      try { await client.publish([previousMic]); } catch { /* noop */ }
    }
    try { ctx.close().catch(() => { /* noop */ }); } catch { /* noop */ }
    setBarking?.(false);
    throw e;
  }

  await new Promise((r) => setTimeout(r, holdMs));
  try {
    try { src.stop(); } catch { /* already finished */ }
    try { await client.unpublish([remoteTrack]); } catch { /* noop */ }
    try { remoteTrack.close(); } catch { /* noop */ }
    try { ctx.close().catch(() => { /* noop */ }); } catch { /* noop */ }
    if (previousMic && micRef.current === previousMic) {
      try { await client.publish([previousMic]); } catch { /* noop */ }
      if (wasMuted) {
        try { await previousMic.setMuted(true); } catch { /* noop */ }
      }
    }
  } finally {
    setBarking?.(false);
  }
}

// Back-compat — keep the original symbol so older imports still work.
// Now points at the sole remaining dog-bark deterrent.
export async function playDogBarkOnCall(args) {
  return playDeterrentOnCall({ ...args, deterrentId: "bark_small" });
}
